import { Fragment, ReactElement } from 'react';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarScrollWrapper,
  SidebarItem,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import {
  NavContentBlueprint,
  NavContentComponentProps,
  NavContentNavItem,
} from '@backstage/plugin-app-react';
import {
  coreExtensionData,
  createExtensionInput,
} from '@backstage/frontend-plugin-api';
import { compatWrapper } from '@backstage/core-compat-api';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import PeopleIcon from '@material-ui/icons/People';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { SidebarLogo } from './SidebarLogo';

/**
 * Structural sidebar chrome — the menu items themselves come from the nav
 * items contributed by plugins (page extensions), picked into position by
 * title below. Internal plugins can instead contribute arbitrary sidebar
 * elements (e.g. submenus) by attaching an extension that outputs
 * `coreExtensionData.reactElement` to `{ id: 'nav-content:app', input:
 * 'sidebarEntries' }` — those render in the custom section without any
 * changes to this file. Page-derived items not explicitly placed fall
 * through to the end of the custom section via `rest()`.
 */
const SidebarContent = ({
  navItems,
  entries,
}: NavContentComponentProps & { entries: ReactElement[] }) => {
  // Page extension ids of legacy-converted routes are generated, so pick
  // items by title instead.
  const take = (title: string): NavContentNavItem | undefined => {
    const item = navItems.rest().find(i => i.title === title);
    return item && navItems.take(item.node.spec.id);
  };

  const item = (title: string) => {
    const navItem = take(title);
    return navItem ? (
      <SidebarItem
        icon={() => navItem.icon}
        to={navItem.href}
        text={navItem.title}
      />
    ) : null;
  };

  const submenuItem = (sourceTitle: string, title: string) => {
    const navItem = take(sourceTitle);
    return navItem ? (
      <SidebarSubmenuItem
        title={title}
        to={navItem.href}
        icon={() => navItem.icon}
      />
    ) : null;
  };

  // Hidden: registration is reachable from the Create... page instead.
  const registerExisting = navItems
    .rest()
    .find(navItem => navItem.href === '/catalog-import');
  if (registerExisting) {
    navItems.take(registerExisting.node.spec.id);
  }

  const insightsItems = (
    <>
      {submenuItem('Tech Insights', 'Tech Insights')}
      {submenuItem('Cost Insights', 'Cost Insights')}
    </>
  );
  const devTools = item('DevTools');
  const feedback = item('Feedback');

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {item('Home')}
        {item('Catalog')}
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={PeopleIcon}
        />
        {item('Catalog Graph')}
        {item('APIs')}
        {item('Docs')}
        {item('Create...')}
        <SidebarDivider />
        <SidebarItem icon={TrendingUpIcon} text="Insights">
          <SidebarSubmenu title="Insights">{insightsItems}</SidebarSubmenu>
        </SidebarItem>
        {entries.map((entry, index) => (
          <Fragment key={index}>{entry}</Fragment>
        ))}
        <SidebarScrollWrapper>
          {navItems
            .withComponent(navItem => (
              <SidebarItem
                icon={() => navItem.icon}
                to={navItem.href}
                text={navItem.title}
              />
            ))
            .rest()}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
      {devTools}
      {feedback}
    </Sidebar>
  );
};

export const sidebarContent = NavContentBlueprint.makeWithOverrides({
  inputs: {
    sidebarEntries: createExtensionInput([coreExtensionData.reactElement]),
  },
  *factory(originalFactory, { inputs }) {
    const entries = inputs.sidebarEntries.map(entry =>
      entry.get(coreExtensionData.reactElement),
    );
    yield* originalFactory({
      component: props =>
        compatWrapper(<SidebarContent {...props} entries={entries} />),
    });
  },
});
